import dayjs from "dayjs";
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DEFAULT_TIME } from "../constant/ReportConstants";
import useLocationHelpers from "../hooks/useLocationHelpers";
import { getReportDefaultDateFilter } from "../helpers/reportSettings";
import { UserContext } from "../amplify/authenticator-provider";
import { checkAccessPermission } from "../library/helpers";
import { AccessPermissionAction } from "../library/constants";
import { setModuleFilters } from "../redux/slices/persistedFiltersSlice";
import { ENTITY_MODE } from "../components/commonComponents/EntityFilter";
import useTeams from "../hooks/useTeams";

const CUSTOM = "custom";
const MODULE_KEY = "engagementDashboard";

export const DashboardFilterContext = createContext();

// `persistFilters` controls whether commits are mirrored back into
// `persistedFilters[engagementDashboard]` — the shared slice the main
// dashboard rehydrates from. Detail screens that only want to *inherit*
// the dashboard scope on mount, without pushing their local edits back,
// mount the provider with `persistFilters={false}`.
export const DashboardFilterProvider = ({ children, persistFilters = true }) => {
  const dispatch = useDispatch();
  const locationId = useSelector(state => state.location.locationId);
  const persistedFilters = useSelector(state => state.persistedFilters?.filters?.[MODULE_KEY]);
  const { currentLocation, isUserLoaded } = useLocationHelpers();
  const { user } = useContext(UserContext);
  // Some users are restricted to viewing only their own data on the dashboard —
  // for them we preselect the current user in the global filter.
  // Two flags pin the logged-in user as the default:
  //   - DASHBOARD_INDIVIDUAL_VIEW: preselect but allow the user to change.
  //   - ACTIVITY_CENTER_RESTRICT_REMOVE_DEFAULT_FILTER: preselect AND lock
  //     the Users/Teams filter so it cannot be changed.
  const hasIndividualViewOnly = checkAccessPermission(
    AccessPermissionAction.DASHBOARD_INDIVIDUAL_VIEW
  );
  const restrictRemoveDefaultFilter = checkAccessPermission(
    AccessPermissionAction.ACTIVITY_CENTER_RESTRICT_REMOVE_DEFAULT_FILTER
  );
  const pinSelfAsDefault = hasIndividualViewOnly || restrictRemoveDefaultFilter;
  const defaultUserIds = useMemo(
    () => (pinSelfAsDefault && user?.id ? [user.id] : []),
    [pinSelfAsDefault, user?.id]
  );
  // True when the Users/Teams filter should be rendered as read-only.
  // Only the restrict flag locks the dropdown; individual-view still lets
  // the user change selection within their permitted scope.
  const isEntityFilterLocked = restrictRemoveDefaultFilter;

  // filterType used by APIs — only updates when we have valid data to fetch
  const [filterType, setFilterType] = useState(DEFAULT_TIME);
  // Opportunity Type filter — single int id (null = no filter). Sent as the
  // `$opportunityTypeId` variable to every dashboard query; mirrors the
  // appointment-page filter so behavior stays consistent.
  const [opportunityTypeId, setOpportunityTypeId] = useState(null);
  // Visible toggle state — what the radio inside the dropdown shows. Switching
  // it alone is a "preview" of the other list and does NOT trigger a refetch.
  const [entityMode, setEntityMode] = useState(ENTITY_MODE.USERS);
  // Committed filter mode — whose IDs actually ride along in the API payload.
  // Only flips when the user makes an explicit selection (see selectUserIds /
  // selectTeamIds below), so a bare toggle leaves the dashboard data intact.
  const [activeFilterMode, setActiveFilterMode] = useState(ENTITY_MODE.USERS);
  // Global user filter
  const [userIds, setUserIds] = useState(defaultUserIds);
  // Global team filter
  const [teamIds, setTeamIds] = useState([]);
  // ISO strings for API consumption
  const [dateRange, setDateRange] = useState([]);
  // TZ-naive "YYYY-MM-DD" strings for UI display only — preserves the picker's
  // selected calendar date across viewer timezones, where the ISO `dateRange`
  // alone cannot (e.g. an IST-picked Mar 1 stores as "...02-28T18:30Z"). The
  // API payload still uses `dateRange`; this is purely a display sidecar.
  const [dateRangeDisplay, setDateRangeDisplay] = useState([]);
  // What the dropdown visually shows (may differ from filterType when custom is pending)
  const [pickerFilterType, setPickerFilterType] = useState(DEFAULT_TIME);
  // dayjs objects for the DateRangeFilter picker
  const [pickerDateRange, setPickerDateRange] = useState(null);
  // Becomes true once defaults have been synced from the saved REPORT_SETTINGS.
  const [initialized, setInitialized] = useState(false);
  const didInit = useRef(false);
  // Tracks the locationId we last reset userIds for. Lets the location-reset
  // effect distinguish a real location change from incidental re-runs (e.g. user
  // loading async and changing defaultUserIds reference), which would otherwise
  // clobber userIds we just rehydrated.
  const prevLocationIdRef = useRef(locationId);
  // True if the user landed with a saved filter snapshot — used by the
  // "auto-default to leader team" effect to avoid overriding the user's
  // own committed selection.
  const hadPersistedFilterRef = useRef(false);
  // Ensures the leader-team auto-default runs at most once per mount.
  const autoDefaultedRef = useRef(false);

  useEffect(() => {
    if (!isUserLoaded || didInit.current) return;
    didInit.current = true;

    if (persistedFilters?.filterType) {
      hadPersistedFilterRef.current = true;
      const {
        filterType: pf,
        dateRange: pdr,
        dateRangeDisplay: pdrd,
        userIds: pUserIds,
        opportunityTypeId: pOpportunityTypeId,
        teamIds: pTeamIds,
        entityMode: pEntityMode,
        activeFilterMode: pActiveFilterMode,
      } = persistedFilters;
      if (pOpportunityTypeId != null) {
        const n = parseInt(pOpportunityTypeId, 10);
        if (Number.isFinite(n)) setOpportunityTypeId(n);
      }
      setFilterType(pf);
      setPickerFilterType(pf);
      setDateRange(Array.isArray(pdr) ? pdr : []);
      setDateRangeDisplay(Array.isArray(pdrd) ? pdrd : []);
      if (pf === CUSTOM && Array.isArray(pdrd) && pdrd.length === 2) {
        // Reconstruct picker dayjs objects from TZ-naive date strings so the
        // picker shows the originally-selected calendar date in every viewer
        // timezone. Fall back to legacy ISO `pdr` for old persisted snapshots.
        setPickerDateRange([dayjs(pdrd[0]), dayjs(pdrd[1])]);
      } else if (pf === CUSTOM && Array.isArray(pdr) && pdr.length === 2) {
        setPickerDateRange([dayjs(pdr[0]), dayjs(pdr[1])]);
      }
      if (isEntityFilterLocked && defaultUserIds?.length > 0) {
        // Locked to own data: ignore the persisted Users/Teams selection (it may
        // be a stale "all users" snapshot saved before the restriction applied)
        // and re-pin the current user. Otherwise the disabled filter would show
        // all users after a hard refresh.
        setUserIds(defaultUserIds);
        setTeamIds([]);
        setEntityMode(ENTITY_MODE.USERS);
        setActiveFilterMode(ENTITY_MODE.USERS);
      } else {
        // Trust the persisted choice — including an empty array, which is the
        // user's deliberate "clear all" selection. Permission default only
        // applies on first-ever load (no persisted snapshot, see else branch).
        setUserIds(Array.isArray(pUserIds) ? pUserIds : defaultUserIds);
        setTeamIds(Array.isArray(pTeamIds) ? pTeamIds : []);
        if (pEntityMode === ENTITY_MODE.USERS || pEntityMode === ENTITY_MODE.TEAMS) {
          setEntityMode(pEntityMode);
        }
        // Restore committed mode separately so a "previewing teams" snapshot
        // doesn't get auto-committed on reload. Falls back to the toggle for
        // older snapshots that didn't persist this field yet.
        if (pActiveFilterMode === ENTITY_MODE.USERS || pActiveFilterMode === ENTITY_MODE.TEAMS) {
          setActiveFilterMode(pActiveFilterMode);
        } else if (pEntityMode === ENTITY_MODE.USERS || pEntityMode === ENTITY_MODE.TEAMS) {
          setActiveFilterMode(pEntityMode);
        }
      }
    } else {
      const { filter, range } = getReportDefaultDateFilter(currentLocation?.settings);
      setPickerFilterType(filter);
      if (filter === CUSTOM && range) {
        setPickerDateRange(range);
        setDateRange([range[0].startOf("day").toISOString(), range[1].endOf("day").toISOString()]);
        setDateRangeDisplay([range[0].format("YYYY-MM-DD"), range[1].format("YYYY-MM-DD")]);
        setFilterType(CUSTOM);
      } else {
        setFilterType(filter);
      }
      // No persisted snapshot — apply the permission-driven default now
      // that user is loaded (useState's initial value runs before user
      // resolves, so userIds would otherwise stay `[]` on first load).
      if (defaultUserIds.length > 0) {
        setUserIds(defaultUserIds);
      }
    }
    setInitialized(true);
  }, [isUserLoaded, currentLocation]);

  useEffect(() => {
    if (!initialized) return;
    if (!persistFilters) return;
    dispatch(
      setModuleFilters({
        moduleKey: MODULE_KEY,
        filters: {
          filterType,
          dateRange,
          dateRangeDisplay,
          userIds,
          teamIds,
          entityMode,
          opportunityTypeId,
          activeFilterMode,
        },
      })
    );
  }, [
    initialized,
    filterType,
    dateRange,
    dateRangeDisplay,
    userIds,
    teamIds,
    entityMode,
    activeFilterMode,
    opportunityTypeId,
  ]);

  const handleDateFilterChange = value => {
    setPickerFilterType(value);
    if (value !== CUSTOM) {
      // Preset filter — update API filter immediately
      setFilterType(value);
      setDateRange([]);
      setDateRangeDisplay([]);
      setPickerDateRange(null);
    }
    // If custom, don't update filterType yet — wait for date range selection
  };

  const handleDateRangeChange = dates => {
    setPickerDateRange(dates);
    if (dates && dates[0] && dates[1]) {
      const startDate = dayjs(dates[0]).startOf("day");
      const endDate = dayjs(dates[1]).endOf("day");
      setDateRange([startDate.toISOString(), endDate.toISOString()]);
      // Sidecar for display — the picker's calendar date, TZ-naive.
      setDateRangeDisplay([
        dayjs(dates[0]).format("YYYY-MM-DD"),
        dayjs(dates[1]).format("YYYY-MM-DD"),
      ]);
      // Now that we have actual dates, update the API filterType
      setFilterType(CUSTOM);
    } else {
      setDateRange([]);
      setDateRangeDisplay([]);
    }
  };

  useEffect(() => {
    if (prevLocationIdRef.current === locationId) return;
    prevLocationIdRef.current = locationId;
    setUserIds(defaultUserIds);
    // Opportunity types are scoped to industry/location; reset on switch so
    // we don't carry a stale id against a location that doesn't have it.
    setOpportunityTypeId(null);
    // A location switch invalidates team membership too — reset so we don't
    // accidentally apply a previous location's team IDs against new data.
    setTeamIds([]);
  }, [locationId, defaultUserIds]);

  // Selection commit: changing the active mode and clearing the opposite
  // mode's state happens here, not on toggle. The radio toggle is a preview
  // (entityMode); only an actual selection commits (activeFilterMode).
  const selectUserIds = ids => {
    setUserIds(ids);
    setTeamIds([]);
    setActiveFilterMode(ENTITY_MODE.USERS);
  };
  const selectTeamIds = ids => {
    setTeamIds(ids);
    setUserIds([]);
    setActiveFilterMode(ENTITY_MODE.TEAMS);
  };

  // Pulled here (in addition to inside EntityFilter) so the context can
  // expand "All teams" into the full id list when computing apiTeamIds, and
  // detect a leader team for the auto-default. Apollo caches the query, so
  // this extra read is a no-op. We use `activeTeams` since the filter must
  // never apply (or auto-default into) a deactivated team.
  const { activeTeams: teams, loading: teamsLoading } = useTeams();

  // Auto-default: on first land (no persisted snapshot) once teams have
  // finished loading, if the current user is the leader of at least one
  // active team, switch to Teams mode and pre-select that team. Runs at
  // most once per mount; never overrides a user's persisted choice.
  useEffect(() => {
    if (autoDefaultedRef.current) return;
    if (!initialized || teamsLoading) return;
    if (hadPersistedFilterRef.current) return;
    if (!user?.id) return;

    const currentUserId = +user.id;
    const leaderTeam = (teams || []).find(t => +t?.leaderId === currentUserId);
    autoDefaultedRef.current = true;
    if (!leaderTeam) return;

    setEntityMode(ENTITY_MODE.TEAMS);
    // Use the committing setter so the activeFilterMode flips too — without
    // this the dashboard would render Teams in the toggle but still send
    // userIds in the API payload.
    setTeamIds([+leaderTeam.id]);
    setUserIds([]);
    setActiveFilterMode(ENTITY_MODE.TEAMS);
  }, [initialized, teamsLoading, teams, user?.id]);

  // Effective IDs sent in the API payload. Driven by `activeFilterMode` (the
  // committed mode) — NOT by `entityMode` (the toggle). So flipping the
  // toggle alone doesn't change the payload, which means no refetch fires
  // until the user actually selects something in the new mode.
  //
  // "All" semantics differ by entity:
  //   • All users → userIds=[] (BE treats empty as no filter = all)
  //   • All teams → teamIds=[every team id] (BE wants the explicit list)
  // The state itself uses [] for both (clean toggle-back behavior); only the
  // outbound API value is expanded for teams.
  const apiUserIds = activeFilterMode === ENTITY_MODE.USERS ? userIds : [];
  const apiTeamIds = useMemo(() => {
    if (activeFilterMode !== ENTITY_MODE.TEAMS) return [];
    if (teamIds.length > 0) return teamIds;
    // teamIds=[] + committed teams = explicit "All teams" → send every id.
    return teams.map(t => +t.id);
  }, [activeFilterMode, teamIds, teams]);

  return (
    <DashboardFilterContext.Provider
      value={{
        filterType,
        dateRange,
        dateRangeDisplay,
        userIds,
        setUserIds: selectUserIds,
        opportunityTypeId,
        setOpportunityTypeId,
        teamIds,
        setTeamIds: selectTeamIds,
        entityMode,
        setEntityMode,
        activeFilterMode,
        apiUserIds,
        apiTeamIds,
        // Surface the fetched team list (active only) + loading flag so
        // EntityFilter can render without re-issuing the same query.
        teams,
        teamsLoading,
        pickerFilterType,
        pickerDateRange,
        handleDateFilterChange,
        handleDateRangeChange,
        isReady: initialized,
        isEntityFilterLocked,
      }}
    >
      {children}
    </DashboardFilterContext.Provider>
  );
};

export const useDashboardFilter = () => {
  const context = useContext(DashboardFilterContext);
  if (!context) {
    throw new Error("useDashboardFilter must be used within a DashboardFilterProvider");
  }
  return context;
};
